import React, { Component } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell, ComposedChart, Line } from 'recharts';
import { Download, AlertTriangle, Shield, Cloud } from 'lucide-react';
// --- Mock Data ---
const catAALData = [{
  year: '2020',
  actual: 420,
  plan: 500
}, {
  year: '2021',
  actual: 480,
  plan: 500
}, {
  year: '2022',
  actual: 520,
  plan: 500
}, {
  year: '2023',
  actual: 450,
  plan: 500
}, {
  year: '2024',
  actual: 490,
  plan: 500
}];
const returnPeriodData = [{
  period: '1-in-10',
  gross: 1200,
  net: 850,
  label: '10-year'
}, {
  period: '1-in-50',
  gross: 3500,
  net: 2100,
  label: '50-year'
}, {
  period: '1-in-100',
  gross: 5800,
  net: 3200,
  label: '100-year'
}, {
  period: '1-in-250',
  gross: 8500,
  net: 4500,
  label: '250-year'
}];
const netCATvsCapitalData = [{
  returnPeriod: '1-in-10',
  netLoss: 850,
  capital: 8500,
  ratio: 10
}, {
  returnPeriod: '1-in-50',
  netLoss: 2100,
  capital: 8500,
  ratio: 25
}, {
  returnPeriod: '1-in-100',
  netLoss: 3200,
  capital: 8500,
  ratio: 38
}, {
  returnPeriod: '1-in-250',
  netLoss: 4500,
  capital: 8500,
  ratio: 53
}];
const perilBreakdownData = [{
  peril: 'Windstorm',
  exposure: 4200,
  aal: 180
}, {
  peril: 'Flood',
  exposure: 2800,
  aal: 120
}, {
  peril: 'Earthquake',
  exposure: 1500,
  aal: 85
}, {
  peril: 'Hail',
  exposure: 900,
  aal: 45
}];
const regionBreakdownData = [{
  region: 'UK South',
  exposure: 3500,
  aal: 150
}, {
  region: 'UK North',
  exposure: 2400,
  aal: 95
}, {
  region: 'Scotland',
  exposure: 1800,
  aal: 80
}, {
  region: 'Wales',
  exposure: 1200,
  aal: 65
}];
// --- Colors ---
const COLORS = {
  actual: '#3B82F6',
  plan: '#9CA3AF',
  gross: '#6B7280',
  net: '#3B82F6',
  capital: '#10B981',
  warning: '#F59E0B',
  critical: '#DC2626' // Red
};
// --- Components ---
const CustomTooltip = ({
  active,
  payload,
  label,
  unit = '£K'
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-md text-sm" data-id="element-1158">
        <p className="font-semibold text-gray-900 mb-2" data-id="element-1159">{label}</p>
        {payload.map((entry: any, index: number) => <div key={index} className="flex items-center gap-2 mb-1 last:mb-0" data-id="element-1160">
            <div className="w-3 h-3 rounded-full" style={{
          backgroundColor: entry.color || entry.fill
        }} data-id="element-1161" />
            <span className="text-gray-600" data-id="element-1162">{entry.name}:</span>
            <span className="font-medium text-gray-900" data-id="element-1163">
              {unit === '£K' ? '£' : ''}
              {entry.value.toLocaleString()}
              {unit === '£K' ? 'K' : unit === '%' ? '%' : ''}
            </span>
          </div>)}
      </div>;
  }
  return null;
};
const SmallMultiple = ({
  title,
  data,
  dataKey
}: {
  title: string;
  data: any[];
  dataKey: string;
}) => {
  return <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1164">
      <h3 className="text-sm font-semibold text-gray-700 mb-3" data-id="element-1165">{title}</h3>
      <div className="space-y-2" data-id="element-1166">
        {data.map((item, index) => {
        const maxValue = Math.max(...data.map(d => d[dataKey]));
        const percentage = item[dataKey] / maxValue * 100;
        return <div key={index} data-id="element-1167">
              <div className="flex justify-between text-xs mb-1" data-id="element-1168">
                <span className="text-gray-600" data-id="element-1169">
                  {item.peril || item.region}
                </span>
                <span className="font-medium text-gray-900" data-id="element-1170">
                  £{item[dataKey]}K
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1171">
                <div className="h-full bg-blue-600 rounded-full transition-all duration-500" style={{
              width: `${percentage}%`
            }} data-id="element-1172" />
              </div>
            </div>;
      })}
      </div>
    </div>;
};
export function CatastropheRisk() {
  const currentAAL = catAALData[catAALData.length - 1].actual;
  const planAAL = catAALData[catAALData.length - 1].plan;
  const in100Loss = returnPeriodData.find(d => d.period === '1-in-100')?.net || 0;
  const in250Loss = returnPeriodData.find(d => d.period === '1-in-250')?.net || 0;
  const capital = 8500;
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-1173">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-1174">
        <div data-id="element-1175">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1176">Catastrophe Risk</h1>
          <p className="text-gray-500 mt-1" data-id="element-1177">
            Tail risk and capital exposure analysis • FY 2024
          </p>
        </div>
        <div className="flex items-center gap-4" data-id="element-1178">
          <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-full border border-blue-200" data-id="element-1179">
            <Shield className="text-blue-600" size={16} data-id="element-1180" />
            <span className="text-sm font-medium text-blue-700" data-id="element-1181">
              Within Risk Appetite
            </span>
          </div>
          <button className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-1182">
            <Download size={16} className="mr-2" data-id="element-1183" />
            Export Report
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1184">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1185">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1186">
            CAT AAL
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-1187">£{currentAAL}K</p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1188">
            {(currentAAL / planAAL * 100).toFixed(0)}% of plan
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1189">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1190">
            1-in-100 Net Loss
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-1191">
            £{(in100Loss / 1000).toFixed(1)}M
          </p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1192">
            {(in100Loss / capital * 100).toFixed(0)}% of capital
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1193">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1194">
            1-in-250 Net Loss
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-1195">
            £{(in250Loss / 1000).toFixed(1)}M
          </p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1196">
            {(in250Loss / capital * 100).toFixed(0)}% of capital
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1197">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1198">
            Available Capital
          </p>
          <p className="text-2xl font-bold text-green-600" data-id="element-1199">
            £{(capital / 1000).toFixed(1)}M
          </p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1200">Adequate coverage</p>
        </div>
      </div>

      {/* Top Row: CAT AAL vs Plan */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1201">
        <div className="flex justify-between items-center mb-6" data-id="element-1202">
          <div data-id="element-1203">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1204">
              CAT Average Annual Loss (AAL) vs Plan
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1205">
              Historical AAL performance against risk appetite
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm" data-id="element-1206">
            <div className="flex items-center gap-2" data-id="element-1207">
              <div className="w-3 h-3 rounded bg-blue-600" data-id="element-1208"></div>
              <span className="text-gray-600" data-id="element-1209">Actual AAL</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1210">
              <div className="w-3 h-0.5 bg-gray-400 border-t border-dashed" data-id="element-1211"></div>
              <span className="text-gray-600" data-id="element-1212">Plan (£500K)</span>
            </div>
          </div>
        </div>
        <div className="h-64" data-id="element-1213">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1214">
            <BarChart data={catAALData} margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-1215">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-1216" />
              <XAxis dataKey="year" axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 12
            }} dy={10} data-id="element-1217" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 12
            }} tickFormatter={value => `£${value}K`} data-id="element-1218" />
              <Tooltip content={<CustomTooltip unit="£K" data-id="element-1220" />} data-id="element-1219" />
              <ReferenceLine y={500} stroke={COLORS.plan} strokeDasharray="5 5" strokeWidth={2} label={{
              value: 'Plan',
              position: 'right',
              fill: '#9CA3AF',
              fontSize: 12
            }} data-id="element-1221" />
              <Bar dataKey="actual" name="Actual AAL" fill={COLORS.actual} radius={[4, 4, 0, 0]} data-id="element-1222" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Middle Row: Return Period Losses */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1223">
        <div className="flex justify-between items-center mb-6" data-id="element-1224">
          <div data-id="element-1225">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1226">
              Return Period Loss Estimates
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1227">
              Gross and net catastrophe losses by return period
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm" data-id="element-1228">
            <div className="flex items-center gap-2" data-id="element-1229">
              <div className="w-3 h-3 rounded bg-gray-600" data-id="element-1230"></div>
              <span className="text-gray-600" data-id="element-1231">Gross Loss</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1232">
              <div className="w-3 h-3 rounded bg-blue-600" data-id="element-1233"></div>
              <span className="text-gray-600" data-id="element-1234">Net Loss</span>
            </div>
          </div>
        </div>
        <div className="h-80" data-id="element-1235">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1236">
            <BarChart data={returnPeriodData} margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 40
          }} data-id="element-1237">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-1238" />
              <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 12
            }} dy={10} data-id="element-1239" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 12
            }} tickFormatter={value => `£${value / 1000}M`} data-id="element-1240" />
              <Tooltip content={<CustomTooltip unit="£K" data-id="element-1242" />} data-id="element-1241" />
              <Bar dataKey="gross" name="Gross Loss" fill={COLORS.gross} radius={[4, 4, 0, 0]} data-id="element-1243" />
              <Bar dataKey="net" name="Net Loss" fill={COLORS.net} radius={[4, 4, 0, 0]} data-id="element-1244" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 grid grid-cols-4 gap-4 text-center text-xs text-gray-500" data-id="element-1245">
          {returnPeriodData.map((item, index) => <div key={index} className="border-l border-gray-200 first:border-0 px-2" data-id="element-1246">
              <p className="font-semibold text-gray-700" data-id="element-1247">{item.label}</p>
              <p className="mt-1" data-id="element-1248">Net: £{(item.net / 1000).toFixed(1)}M</p>
              <p className="text-gray-400" data-id="element-1249">
                {(item.net / capital * 100).toFixed(0)}% of capital
              </p>
            </div>)}
        </div>
      </div>

      {/* Bottom Row: Net CAT Loss vs Capital + Small Multiples */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1250">
        {/* Net CAT Loss vs Capital */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1251">
          <h2 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-1252">
            Net CAT Loss vs Available Capital
          </h2>
          <div className="h-64" data-id="element-1253">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1254">
              <ComposedChart data={netCATvsCapitalData} margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-1255">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-1256" />
                <XAxis dataKey="returnPeriod" axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} dy={10} data-id="element-1257" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} tickFormatter={value => `£${value / 1000}M`} data-id="element-1258" />
                <Tooltip content={<CustomTooltip unit="£K" data-id="element-1260" />} data-id="element-1259" />
                <Legend verticalAlign="top" align="right" wrapperStyle={{
                paddingBottom: '20px'
              }} iconType="circle" data-id="element-1261" />
                <ReferenceLine y={8500} stroke={COLORS.capital} strokeDasharray="5 5" strokeWidth={2} label={{
                value: 'Available Capital',
                position: 'right',
                fill: '#10B981',
                fontSize: 12
              }} data-id="element-1262" />
                <Bar dataKey="netLoss" name="Net CAT Loss" fill={COLORS.net} radius={[4, 4, 0, 0]} data-id="element-1263" />
                <Line type="monotone" dataKey="capital" name="Capital" stroke={COLORS.capital} strokeWidth={0} dot={false} data-id="element-1264" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-4 text-sm text-gray-500" data-id="element-1265">
            Even at 1-in-250 year event, net loss represents only{' '}
            <span className="font-semibold text-gray-900" data-id="element-1266">53%</span> of
            available capital
          </p>
        </div>

        {/* Small Multiples */}
        <div className="space-y-4" data-id="element-1267">
          <SmallMultiple title="Exposure by Peril" data={perilBreakdownData} dataKey="aal" data-id="element-1268" />
          <SmallMultiple title="Exposure by Region" data={regionBreakdownData} dataKey="aal" data-id="element-1269" />
        </div>
      </div>
    </div>;
}